// client/src/pages/MapView/components/PostalCodeMapLeaflet.js
// Leaflet-karta med heatmap-lager för postnummer-rapporter. Använder
// setLatLngs() för smooth animation; tar om-create vid config-change.
import React, { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './PostalCodeMapLeaflet.css';

// ===== CONFIG =====
const HEATMAP_CONFIG = {
  radius: 28,
  blur: 20,
  maxZoom: 17,
  max: 1.0,
  minOpacity: 0.50,
  gradient: {
    0.0: '#2196f3',
    0.15: '#00bcd4',
    0.30: '#4caf50',
    0.45: '#cddc39',
    0.55: '#ffeb3b',
    0.65: '#ff9800',
    0.78: '#f44336',
    0.90: '#e91e63',
    1.0: '#ad1457'
  }
};

// ===== HEATMAP LAYER COMPONENT =====
// ✅ Optimerad: setLatLngs() för smooth animation, remove+create vid config change
const HeatmapLayer = ({ heatPoints, config = HEATMAP_CONFIG }) => {
  const map = useMap();
  const heatLayerRef = useRef(null);
  const configRef = useRef(config);
  
  useEffect(() => {
    if (!window.L?.heatLayer) return;
    
    // Tom data → rensa
    if (!heatPoints || heatPoints.length === 0) {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
      return;
    }
    
    // Config changed → recreate layer
    const configChanged = JSON.stringify(config) !== JSON.stringify(configRef.current);
    if (configChanged && heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
      configRef.current = config;
    }
    
    // Layer finns redan → uppdatera in-place (snabbt, 60fps-vänligt)
    if (heatLayerRef.current) {
      heatLayerRef.current.setLatLngs(heatPoints);
      return;
    }
    
    // Skapa layer med aktuell config
    const layer = window.L.heatLayer(heatPoints, config);
    layer.addTo(map);
    heatLayerRef.current = layer;
    configRef.current = config;
    
    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
    };
  }, [map, heatPoints, config]);
  
  return null;
};

// ===== MAP CENTER UPDATER =====
const MapCenterUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && Array.isArray(center)) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);
  
  return null;
};

// ===== MAIN COMPONENT =====
const PostalCodeMapLeaflet = ({
  data = [],
  mapCenter = [62.2, 16.5],
  zoom = 5,
  visualizationType = 'heatmap',
  loading = false,
  heatmapConfig = null // Optional override for demo/cloud mode
}) => {
  
  // Merge custom config with defaults
  const activeConfig = useMemo(() => {
    if (!heatmapConfig) return HEATMAP_CONFIG;
    return { ...HEATMAP_CONFIG, ...heatmapConfig };
  }, [heatmapConfig]);
  
  // Ladda leaflet.heat plugin
  useEffect(() => {
    if (window.L?.heatLayer) return;
    
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.heat/0.2.0/leaflet-heat.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);
  
  // Konvertera data till heatPoints
  const heatPoints = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // ✅ Steg 1: Filtrera valid items
    const validItems = data.filter(item => {
      const count = item.symptomCount || item.count || 0;
      const pos = item?.position;
      const hasPosition = pos && (
        Array.isArray(pos) ||
        (typeof pos === 'object' && pos.lat != null && pos.lng != null)
      );
      return hasPosition && count > 0;
    });
    
    if (validItems.length === 0) return [];
    
    // ✅ Steg 2: Dynamisk max — högsta värdet blir alltid röd/lila
    const maxCount = Math.max(...validItems.map(item => item.symptomCount || item.count || 0));
    
    // ✅ Steg 3: Beräkna intensitet relativt till max
    const points = validItems.map(item => {
      const lat = Array.isArray(item.position) ? item.position[0] : item.position.lat;
      const lng = Array.isArray(item.position) ? item.position[1] : item.position.lng;
      const count = item.symptomCount || item.count || 0;
      const normalized = count / maxCount; // 0.0 → 1.0 relativt till högsta
      const intensity = Math.pow(normalized, 0.65); // Gentler curve → more mid-range color
      return [lat, lng, Math.max(intensity, 0.20)]; // Floor 0.20 → even low areas show soft color
    });
    
    console.log('🗺️ HeatPoints:', points.length, 'of', data.length, '| maxCount:', maxCount);
    return points;
  }, [data]);
  
  // Center som array
  const center = useMemo(() => {
    if (Array.isArray(mapCenter)) return mapCenter;
    if (mapCenter?.lat && mapCenter?.lng) return [mapCenter.lat, mapCenter.lng];
    return [62.2, 16.5];
  }, [mapCenter]);

  // ✅ FIX: Rendera ALLTID HeatmapLayer - den hanterar tom data internt
  const showHeatmap = visualizationType === 'heatmap';

  return (
    <div className="postal-code-map-leaflet">
      {loading && (
        <div className="map-loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">🔄</div>
            <p>Laddar kartdata...</p>
          </div>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={zoom}
        className="leaflet-map-container"
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap &copy; CARTO'
        />
        
        <MapCenterUpdater center={center} zoom={zoom} />
        
        {/* ✅ FIX: Ingen key-trick, alltid rendera - komponenten hanterar tom data */}
        {showHeatmap && <HeatmapLayer heatPoints={heatPoints} config={activeConfig} />}
      </MapContainer>

      <div className="map-legend">
        <h4>🔥 Intensitet</h4>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#e91e63' }}></span>
          <span>Kritisk</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#f44336' }}></span>
          <span>Hög</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ff9800' }}></span>
          <span>Medel</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#4caf50' }}></span>
          <span>Låg</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#2196f3' }}></span>
          <span>Minimal</span>
        </div>
        <div className="legend-divider"></div>
        <div className="legend-stats">
          <span>{heatPoints.length} aktiva områden</span>
        </div>
      </div>
    </div>
  );
};

export default PostalCodeMapLeaflet;
